/* eslint-disable require-unicode-regexp */
/** @jsx jsx */
import { useEffect, useState, useMemo, useCallback, Fragment } from 'react'
import { jsx, Flex, Heading, Box, Image, Text } from 'theme-ui'
import { useLocation } from 'react-use'

import { space } from '~/gatsby-plugin-theme-ui'

import { ProductItemComponentType, ShopifyImageSource } from '~/@types/models'

import ProductItemVariantsList, {
  TooltipImageLayout,
} from '~/components/product-item-variants-list'

import { mapColorOptionsWithAvailability } from '../parameters/util'

import { ProductItemsProps, ImageSize, TitleNotePosition, imageSizeMap } from './types'

import ImageWrapper from '~/components/image-wrapper'
import { Link } from '~/componentsV2/linkPrev/LinkPrev'

import { ProductLabel, ProductLabelGroup } from '~/components/product-label'

import { useProductLink } from './hook'
import useProductVariantInfo from '~/hooks/use-product-variant-info'

import {
  addScaleClasses,
  isNoScale,
  imageClassByType,
  scaleImageStyles,
  isRugProduct,
} from '~/utils/product'
import useMediaQuery from '~/hooks/use-media-query'
import { useCollectionContext } from '~/context/collection'
import ProductItemVariantsListV2 from '~/componentsV2/product/product-item-variants-list'
import { useQueryParams } from '~/hooks/use-query-params'
import { useVariantPrices } from '~/hooks/use-product-prices'

const labelVariants = {
  newArrival: {
    name: 'New',
    color: '#001f39',
  },
}

const getLabels = (product: ProductItemComponentType) => {
  if (!product.labels) {
    return []
  }

  const labelsArray = []
  for (const [labelName, value] of Object.entries(product.labels)) {
    if (
      value &&
      labelVariants[labelName as keyof typeof labelVariants] &&
      labelName !== 'newArrival'
    ) {
      labelsArray.push(labelVariants[labelName as keyof typeof labelVariants])
    }
  }
  return labelsArray
}

const ProductItem = ({
  variant = 'primary',
  product,
  visibleVariantsCount,
  productImageRatio = 1,
  imageSize = ImageSize.FULLSCREEN,
  titleNotePosition = TitleNotePosition.BELOW_TITLE,
  productVariant,
  collectionHandle,
  isNoScaleCustom = false,
  mobileUseZoom,
  onlyCompareAtPriceVariants,
  footerNoMargin = false,
  gridLayout = 'template-default',
  tag,
  hiddenInfo = false,
  fontSize = 20,
  template = 'default',
  onItemClick,
}: ProductItemsProps) => {
  const variantsWithAvailability = mapColorOptionsWithAvailability(
    product,
    product.colorOptions || product.colorOptionsFinish,
    [],
    onlyCompareAtPriceVariants
  )

  const { queryParams } = useQueryParams()
  const search: string = queryParams?.search as string

  const hiddenPrice = useMemo(() => {
    const isSectional = product.title.toLowerCase().includes('sectional')
    const isAntiqueRug = product.productType === 'Antique Rugs'

    return isSectional || isAntiqueRug
  }, [product.productType, product.title])

  const isMobile = useMediaQuery({
    query: breakpoints => `(max-width: ${breakpoints[2]})`,
  })

  const { showZoomImageMobile } = useCollectionContext()
  const zoomImageMobile = product.productType.toLowerCase().includes('rug')

  const variants = variantsWithAvailability?.filter(v => !v.hideColor)

  const currentVariant = useMemo(() => {
    let currentVariant

    if (variants && onlyCompareAtPriceVariants) {
      const [current] = product.variants.filter(variant => {
        const [firstVariant] = variants
        const [firstSelectedOption] = variant?.selectedOptions

        const currentColor = firstVariant?.title
        return firstSelectedOption.value === currentColor
      })

      currentVariant = current
    }

    return currentVariant
  }, [variants, onlyCompareAtPriceVariants, product.variants])

  const isRug = useMemo(() => {
    return isRugProduct(product)
  }, [product])

  const { rangePrice } = useVariantPrices({
    product,
    isStartingAt: true,
    productVariant,
    rangePrice: true,
  })

  // if (product?.title === 'Palmer Sofa') {
  //   console.log(product)
  // }

  const { linkProps, productLink } = useProductLink({
    product,
    currentVariant,
    collectionHandle,
  })

  const [productImages, setProductImages] = useState<ShopifyImageSource[]>(product.images)
  const [imageFromVariants, setImageFromVariants] = useState(product.mainImage)

  const handleImage = useCallback(() => {
    if (!search) {
      if (showZoomImageMobile && product?.images[1]) {
        return {
          sourceImg: product?.images[1].srcDesktopMedium.replace('2344x', '1000x'),
          altText: product?.images[1].altText,
        }
      }
      if (isMobile) {
        return zoomImageMobile
          ? {
              sourceImg:
                product?.images[1].srcMobile?.replace('1125x', '1000x') ||
                imageFromVariants.srcMobileSmall ||
                product?.mainImage.srcMobileSmall,
              altText: product?.images[1].altText,
            }
          : {
              sourceImg: imageFromVariants.srcMobileSmall,
              altText: imageFromVariants.altText,
            }
      }
      return gridLayout !== 'template-rugs'
        ? {
            sourceImg: imageFromVariants?.srcMobile,
            altText: imageFromVariants?.altText,
          }
        : {
            sourceImg: imageFromVariants?.srcMobile?.replace('1125x', '1000x'),
            altText: imageFromVariants?.altText,
          }
    } else {
      if (isMobile) {
        return { sourceImg: imageFromVariants.srcMobileSmall, altText: imageFromVariants.altText }
      } else {
        return { sourceImg: imageFromVariants.srcDesktopMedium, altText: imageFromVariants.altText }
      }
    }
  }, [imageFromVariants])

  const { sourceImg, altText } = useMemo(() => {
    return handleImage()
  }, [imageFromVariants])

  const colorsOptions = useMemo(() => {
    if (product.colorOptions && product.colorOptions.length > 0) {
      return product.colorOptions
    }
    if (product.colorOptionsFinish && product.colorOptionsFinish.length > 0) {
      return product.colorOptionsFinish
    }
    return null
  }, [product])

  const hasVariantImages = useMemo(
    () => product.variantImages && product.variantImages.length > 0,
    [product]
  )

  const showColorNote = (type: TitleNotePosition) => {
    return titleNotePosition === type && product.colorName
  }

  const labels = getLabels(product)

  const showQtyTag = useMemo(() => {
    const currentProduct = !product?.quantityAvailable
      ? product.variants.length === 1
        ? product.variants[0]
        : null
      : product

    if (!currentProduct) {
      return false
    }
    return currentProduct.quantityAvailable <= 3 && currentProduct.quantityAvailable > 0
      ? currentProduct.quantityAvailable
      : false
  }, [product])

  useEffect(() => {
    if (currentVariant) {
      const { altText, id, ...imageRest } = currentVariant.image

      if (currentVariant?.imagesBySKU) {
        setProductImages(currentVariant?.imagesBySKU[0].images)
      }

      setImageFromVariants({
        id,
        altText,
        ...imageRest,
      })
    }
  }, [currentVariant])

  const applyScale = !isNoScale(product) && !isNoScaleCustom

  const { pathname } = useLocation()

  function reorderVariantsByColor(variants: any[], colorName?: string) {
    if (!colorName) {
      return variants
    }
    return variants.sort((a, b) => {
      const aIsColor = a.title.includes(colorName)
      const bIsColor = b.title.includes(colorName)

      if (aIsColor === bIsColor) {
        return 0
      }

      if (aIsColor) {
        return -1
      }

      return 1
    })
  }

  // if (product.title !== 'Oxford Ottoman') {
  //   return null
  // }

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        position: 'relative',
        height: '100%',
        width: '100%',
      }}
      onContextMenu={() => onItemClick && onItemClick()}
      onClick={() => onItemClick && onItemClick()}
    >
      {imageFromVariants && (
        <Link
          to={productLink}
          state={{ prevPath: pathname }}
          sx={applyScale ? scaleImageStyles : null}
          style={{
            position: 'relative',
          }}
          {...linkProps}
          className={`${applyScale ? addScaleClasses(product) : ``}`}
        >
          {tag || showQtyTag ? (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                padding: '4px 8px',
                fontSize: '11px',
                background: 'rgb(214 214 214 / 39%)',
                backdropFilter: 'blur(10px)',
                color: '#000',
                zIndex: 1,
                borderRadius: '4px',
                textTransform: 'uppercase',
              }}
            >
              {showQtyTag
                ? `ONLY ${showQtyTag} LEFT`
                : tag.replace(/"/g, '').replace(/[\\[\]]/g, '')}
            </Box>
          ) : null}
          <ImageWrapper ratio={productImageRatio} center>
            <Image
              className={`${imageClassByType(product)} ${product.handle}`}
              src={sourceImg}
              loading="lazy"
              variant={imageSizeMap[imageSize]}
              alt={imageFromVariants.altText || product.title || 'Product Image'}
              onMouseOver={(e: { currentTarget: { src: string } }) => {
                pathname !== '/search' &&
                  product.images[1] &&
                  (e.currentTarget.src = showZoomImageMobile
                    ? imageFromVariants?.srcMobile
                    : product.images[1].srcDesktopMedium)
              }}
              onMouseOut={(e: { currentTarget: { src: string } }) => {
                e.currentTarget.src = sourceImg
              }}
            />
            {/* Removed on BSOLTS-37-shown-in-issue */}
            {/* {showColorNote(TitleNotePosition.IMAGE_BOTTOM) && (
              <Text
                as="p"
                variant="small"
                sx={{
                  position: 'absolute',
                  bottom: [space.sm],
                  right: [space.md1],
                  color: 'txt',
                }}
              >
                Shown in {product.colorName}
              </Text>
            )} */}
          </ImageWrapper>
          {labels && labels.length > 0 && (
            <ProductLabelGroup>
              {labels.map(label => (
                <ProductLabel key={label.name} label={label.name} color={label.color} />
              ))}
            </ProductLabelGroup>
          )}
        </Link>
      )}

      <Flex
        as="footer"
        sx={{
          flexDirection: 'column',
          mt: '10px',
          // mb: footerNoMargin ? '0' : ['0', space.gridGap2],
          p: ['4px', 0],
          gap: [8, null, null, 12],
          height: '100%',
        }}
      >
        {product.optionsToShow ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                colorsOptions || hasVariantImages ? ['50px 1fr', null, '80px 1fr'] : '1fr',
            }}
          >
            <Fragment>
              {colorsOptions && (
                <ProductItemVariantsListV2
                  gridLayout={gridLayout}
                  inStockColorOption={colorsOptions?.length > 4 ? 4 : colorsOptions?.length}
                  variants={reorderVariantsByColor(colorsOptions, product.colorName)}
                  visibleVariantsCount={variants?.length > 4 ? 4 : variants?.length}
                  {...(isRugProduct(product) && {
                    tooltipLayout: TooltipImageLayout.COVER,
                  })}
                  isRug={isRug}
                />
              )}
              {hasVariantImages && (
                <ProductItemVariantsListV2
                  gridLayout={gridLayout}
                  variants={reorderVariantsByColor(product.variantImages, product.colorName)}
                  visibleVariantsCount={
                    product?.variantImages?.length > 4 ? 4 : product.variantImages.length
                  }
                  {...(isRugProduct(product) && {
                    tooltipLayout: TooltipImageLayout.COVER,
                  })}
                  isRug={isRug}
                />
              )}
            </Fragment>
            <Box>
              {product.optionsToShow.map((option, index) => {
                return (
                  <Text
                    as="p"
                    sx={{
                      variant: `variants.product.${variant}.option`,
                      lineHeight: 1.15,
                      fontSize: ['10px', null, '14px', '16px'],
                      fontWeight: 400,
                      color: ['#2b2b2b'],
                      marginBottom: ['5px', '10px'],
                      paddingRight: [0, '10px'],
                    }}
                    key={index}
                  >
                    <strong style={{ textTransform: 'uppercase' }}>{option.name}</strong> |{' '}
                    {option.value}
                  </Text>
                )
              })}
            </Box>
          </Box>
        ) : (
          <Fragment>
            {colorsOptions && (
              <ProductItemVariantsListV2
                gridLayout={gridLayout}
                inStockColorOption={colorsOptions?.length > 4 ? 4 : colorsOptions?.length}
                variants={reorderVariantsByColor(colorsOptions, product.colorName)}
                visibleVariantsCount={colorsOptions?.length > 4 ? 4 : colorsOptions?.length}
                {...(isRugProduct(product) && {
                  tooltipLayout: TooltipImageLayout.COVER,
                })}
                isRug={isRug}
              />
            )}
            {hasVariantImages && (
              <ProductItemVariantsListV2
                gridLayout={gridLayout}
                variants={reorderVariantsByColor(product.variantImages, product.colorName)}
                visibleVariantsCount={
                  product?.variantImages?.length > 4 ? 4 : product.variantImages.length
                }
                {...(isRugProduct(product) && {
                  tooltipLayout: TooltipImageLayout.COVER,
                })}
                isRug={isRug}
              />
            )}
          </Fragment>
        )}
        <Link
          to={productLink}
          sx={{
            textDecoration: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
          {...linkProps}
        >
          <Heading
            as="h3"
            sx={{
              variant: `variants.product.${variant}.title`,
              lineHeight: 1.15,
              textAlign: 'left',
              textTransform: 'uppercase',
              fontSize: ['12px', null, fontSize + 'px'],
              fontWeight: 400,
              color: ['#2b2b2b'],
              marginBottom: ['5px', '15px'],
              paddingRight: ['5px', '10px'],
              mb: [0, 0, 0],
            }}
          >
            {product.title}
          </Heading>
        </Link>
        {hiddenInfo ? null : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: ['column', 'row'],
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                minHeight: '22px',
                width: 'auto',
                fontSize: [
                  Math.min(12, fontSize - 10) + 'px',
                  null,
                  Math.min(14, fontSize - 10) + 'px',
                  Math.min(14, fontSize - 8) + 'px',
                ],
              }}
            >
              {!hiddenPrice ? rangePrice : null}
            </Box>{' '}
          </Box>
        )}
      </Flex>
    </Flex>
  ) as JSX.Element
}
export default ProductItem
